/**
 =========================================================
 * Material Dashboard 2 PRO React - v2.1.0
 =========================================================

 * Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
 * Copyright 2022 Creative Tim (https://www.creative-tim.com)

 Coded by www.creative-tim.com

 =========================================================

 * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
 */

import { useState } from "react";
import { Form, Formik } from "formik";

import validations from "./schemas/validations";
import form from "./schemas/form";
import initialValues from "./schemas/initialValues";

// @mui material components
import Grid from "@mui/material/Grid";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "views/LayoutContainers/DashboardLayout";
import DashboardNavbar from "views/Navbars/DashboardNavbar";
import Footer from "views/Footer";
import EventInfo from "./components/EventInfo";
import Media from "./components/Media";
import Socials from "./components/Socials";
import Address from "./components/Address";
import { useCreateEventMutation } from "../../../../store/api/events";
import { notificationActions } from "../../../../store/notification/notification-slice";
import { useDispatch } from "react-redux";
import { format } from "date-fns";
import eventDefault from "../../../../assets/images/defaults/event_default.jpg";


// NewProduct page components

function getSteps() {
  return ["1. Event Info", "2. Media", "3. Social", "4. Address"];
}

function getStepContent(stepIndex, formData) {
  switch (stepIndex) {
    case 0:
      return <EventInfo formData={formData} />;
    case 1:
      return <Media formData={formData} />;
    case 2:
      return <Socials formData={formData} />;
    case 3:
      return <Address formData={formData} />;
    default:
      return null;
  }
}

function NewEvent() {
  const [createEvent] = useCreateEventMutation();

  const [activeStep, setActiveStep] = useState(0);

  const steps = getSteps();
  const { formId, formField } = form;
  const currentValidation = validations[activeStep];

  const isLastStep = activeStep === steps.length - 1;

  // const handleNext = () => setActiveStep(activeStep + 1);
  const handleBack = () => setActiveStep(activeStep - 1);
  const dispatch = useDispatch();
  const submitForm = async (values, actions) => {
    dispatch(notificationActions.showLoading(true));
    const save_event = new FormData();
    values.name && save_event.append("name", values.name);
    values.start_date && save_event.append("start_date", values.start_date.toISOString());
    values.end_date && save_event.append("end_date", values.end_date.toISOString());
    values.description && save_event.append("description", values.description);
    values.event_type && save_event.append("event_type", values.event_type);
    values.will_process && save_event.append("will_process", values.will_process);
    values.cover.name && save_event.append("cover", values.cover);
    values.address && save_event.append("address", values.address);
    values.city && save_event.append("city", values.city);
    values.state && save_event.append("state", values.state);
    values.lat && save_event.append("lat", values.lat);
    values.lon && save_event.append("lon", values.lon);
    values.website && save_event.append("website", values.website);
    values.twitter && save_event.append("twitter", values.twitter);
    values.facebook && save_event.append("facebook", values.facebook);
    values.instagram && save_event.append("instagram", values.instagram);
    try {
      const payload = await createEvent(save_event).unwrap();
      dispatch(notificationActions.showMessage({ message: "created successfully", variant: "success" }));
      actions.setSubmitting(false);
      actions.resetForm();
      setActiveStep(0);
    } catch (e) {
      // 500 internal
      // for (const [key, value] of Object.entries(e.data)) {
      //   dispatch(notificationActions.showMessage({ message: key + ": " + value, variant: "error" }));
      // }
    }
    dispatch(notificationActions.showLoading(false));
  };

  const handleSubmit = (values, actions) => {
    if (isLastStep) {
      submitForm(values, actions);
    } else {
      setActiveStep(activeStep + 1);
      actions.setTouched({});
      actions.setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={8} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  Add New Event
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="regular" color="secondary">
                This information will describe more about the product.
              </MDTypography>
            </MDBox>
            <Formik
              initialValues={initialValues}
              validationSchema={currentValidation}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form id={formId} autoComplete="off">
                  <Card>
                    <MDBox mt={-3} mb={3} mx={2}>
                      <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                          <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    </MDBox>
                    <MDBox p={2}>
                      <MDBox>
                        {getStepContent(activeStep, {
                          values,
                          touched,
                          formField,
                          errors,
                          setFieldValue
                        })}
                        <MDBox mt={3} width="100%" display="flex" justifyContent="space-between">
                          {activeStep === 0 ? (
                            <MDBox />
                          ) : (
                            <MDButton variant="gradient" color="light" onClick={handleBack}>
                              back
                            </MDButton>
                          )}
                          <MDButton
                            // disabled={isSubmitting}
                            type="submit"
                            variant="gradient"
                            color="dark"
                          >
                            {isLastStep ? "send" : "next"}
                          </MDButton>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default NewEvent;
